import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ChannelListTableRow from './ChannelListTableRow';
import { selectRoomAlertDeviceList } from '../../redux/slices/deviceList/deviceListSlice';
import ListSortType from '../../utility/types/listSortType';
import SortableTableHeader from '../common/SortableTableHeader/SortableTableHeader';
import {
  fetchFilteredChannelList,
  selectActiveChannelSearchParams,
  setAppliedChannelSearch,
} from '../../redux/slices/channelListSlice/channelListSlice';

const columnLabelMap = [
  { label: 'DeviceName', displayLabel: 'Device Name', sortType: ListSortType.DEVICE_NAME },
  { label: 'SensorName', displayLabel: 'Sensor Name', sortType: ListSortType.SENSOR_NAME },
  { label: 'ComponentName', displayLabel: 'Component Name', sortType: ListSortType.COMPONENT_NAME },
  { label: 'SensorType', displayLabel: 'Sensor Type', sortType: ListSortType.SENSOR_TYPE },
  { label: 'SensorChannel', displayLabel: 'Sensor Channel', sortType: ListSortType.NULLENUM },
  { label: 'Port', displayLabel: 'Port', sortType: ListSortType.NULLENUM },
  { label: 'LastReading', displayLabel: 'Last Reading', sortType: ListSortType.NULLENUM },
  { label: 'LastReceived', displayLabel: 'Last Received', sortType: ListSortType.LAST_RECEIVED },
  { label: 'DeviceType', displayLabel: 'Device Type', sortType: ListSortType.DEVICE_TYPE },
  { label: 'MACAddress', displayLabel: 'MAC Address', sortType: ListSortType.MAC_ADDRESS },
  { label: 'SerialNumber', displayLabel: 'Serial Number', sortType: ListSortType.SERIAL_NUMBER },
  { label: 'Alerts', displayLabel: 'Alerts', sortType: ListSortType.ALERT_COUNT },
];

/* istanbul ignore next */
function ChannelListTable({
  onAddChannel,
  onShowAlerts, channelList, channelListColumnVisibility,
}) {
  const dispatch = useDispatch();
  const activeSearchParams = useSelector(selectActiveChannelSearchParams);

  const handleGetSortedList = (sort, sortType) => {
    const nextSearchParams = {
      ...activeSearchParams,
      sort,
      sortType,
      filters: Array.isArray(activeSearchParams.filters) ? activeSearchParams.filters : [],
    };

    dispatch(fetchFilteredChannelList(nextSearchParams));
    dispatch(setAppliedChannelSearch(nextSearchParams));
  };

  const deviceList = useSelector(selectRoomAlertDeviceList);
  const hasMaxDevice = deviceList.find((d) => d.type === 'RAMAX');
  let columns = columnLabelMap;
  if (!hasMaxDevice) {
    columns = columns.filter((col) => col.label !== 'ComponentName');
  }
  return (
    <>
      <table className="table table-sm table-condensed table-striped my-1">
        <thead className="thead-light">
          <tr>
            <th>{' '}</th>
            {
            columns.map((col) => {
              if (channelListColumnVisibility[col.label] !== true) return null;

              if (col.sortType === ListSortType.NULLENUM) {
                return (
                  <th key={col.label} className="align-content-center">
                    <div>
                      {col.displayLabel}
                    </div>
                  </th>
                );
              }

              return (
                <React.Fragment key={col.label}>
                  <SortableTableHeader
                    onDescClick={() => handleGetSortedList('DES', col.sortType)}
                    label={col.displayLabel}
                    onAscClick={() => handleGetSortedList('ASC', col.sortType)}
                    isSelected={col.sortType === activeSearchParams.sortType}
                  />
                </React.Fragment>
              );
            })
          }
          </tr>
        </thead>
        <tbody>
          {channelList.map((channel, index) => (
            <ChannelListTableRow
              key={channel.channelKey || index}
              channel={channel}
              onAddClick={() => onAddChannel(channel)}
              onAlertsClick={onShowAlerts}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

ChannelListTable.propTypes = {
  onAddChannel: PropTypes.func.isRequired,
  onShowAlerts: PropTypes.func.isRequired,
  channelList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      sensorName: PropTypes.string.isRequired,
      sensorType: PropTypes.string.isRequired,
      sensorChannel: PropTypes.string.isRequired,
      port: PropTypes.string.isRequired,
      deviceName: PropTypes.string.isRequired,
      lastReading: PropTypes.string.isRequired,
      lastReceived: PropTypes.string.isRequired,
      deviceType: PropTypes.string.isRequired,
      macAddress: PropTypes.string.isRequired,
      serialNumber: PropTypes.string.isRequired,
      alertCount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  channelListColumnVisibility: PropTypes.shape({
    SensorName: PropTypes.bool.isRequired,
    SensorType: PropTypes.bool.isRequired,
    SensorChannel: PropTypes.bool.isRequired,
    Port: PropTypes.bool.isRequired,
    DeviceName: PropTypes.bool.isRequired,
    LastReading: PropTypes.bool.isRequired,
    LastReceived: PropTypes.bool.isRequired,
    DeviceType: PropTypes.bool.isRequired,
    MACAddress: PropTypes.bool.isRequired,
    SerialNumber: PropTypes.bool.isRequired,
    Alerts: PropTypes.bool.isRequired,
  }).isRequired,
};

export default ChannelListTable;
